import React from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

interface JobCardFormState {
  operator: string;
  designer: string;
  machine: string;
  material: string;
  finishingInstructions: string;
  priority: string;
  notes: string;
}

interface JobCardModalProps {
  show: boolean;
  onHide: () => void;
  isEditingJobCard: boolean;
  selectedJobCard: any;
  selectedOrder: any;
  jobCardForm: JobCardFormState;
  setJobCardForm: React.Dispatch<React.SetStateAction<JobCardFormState>>;
  jobCardError: string;
  onSaveJobCard: (e: React.FormEvent) => Promise<void>;
  confirmSaveJobCardId: string | null;
}

const JobCardModal: React.FC<JobCardModalProps> = ({
  show,
  onHide,
  isEditingJobCard,
  selectedJobCard,
  selectedOrder,
  jobCardForm,
  setJobCardForm,
  jobCardError,
  onSaveJobCard,
  confirmSaveJobCardId,
}) => {
  return (
    <Modal show={show} onHide={onHide} centered className="admin-jobcard-modal">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-bold text-dark">
          {isEditingJobCard ? `Edit Job Card - ${selectedJobCard?.jobCardId}` : `Create Job Card for ${selectedOrder?.orderId}`}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSaveJobCard}>
        <Modal.Body className="p-4">
          {jobCardError && (
            <Alert variant="danger" className="border-0 py-2 rounded-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '12.5px' }}>
              <i className="fas fa-exclamation-triangle"></i>
              <div>{jobCardError}</div>
            </Alert>
          )}

          {!isEditingJobCard && selectedOrder && (
            <div className="bg-light p-3 rounded-4 border border-light mb-3" style={{ fontSize: '12.5px' }}>
              <div className="fw-bold text-dark border-bottom pb-1.5 mb-2">Customer & Order Details</div>
              <div><strong>Customer:</strong> {selectedOrder.customerName}</div>
              <div><strong>Phone:</strong> {selectedOrder.customerPhone}</div>
              <div className="mt-1">
                <strong>Items:</strong>
                {selectedOrder.items && selectedOrder.items.map((item: any, idx: number) => (
                  <div key={idx} className="ps-2 text-muted" style={{ fontSize: '11.5px' }}>
                    - {item.quantity}x {item.productName}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isEditingJobCard && selectedJobCard && (
            <div className="bg-light p-3 rounded-4 border border-light mb-3" style={{ fontSize: '12.5px' }}>
              <div className="fw-bold text-dark border-bottom pb-1.5 mb-2">Update Information</div>
              <div><strong>Last Updated:</strong> {new Date(Number(selectedJobCard.updatedAt) || selectedJobCard.updatedAt).toLocaleString()}</div>
              <div><strong>Updated By:</strong> {selectedJobCard.updatedBy || 'System'}</div>
            </div>
          )}

          <Form.Group className="mb-3" controlId="formJobCardOperator">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Print Operator Assignee</Form.Label>
            <Form.Control
              type="text"
              placeholder="Name of operator (e.g. Ramesh)"
              value={jobCardForm.operator}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, operator: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardDesigner">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Graphic Designer Assignee</Form.Label>
            <Form.Control
              type="text"
              placeholder="Name of graphic designer (e.g. Suresh)"
              value={jobCardForm.designer}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, designer: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardMachine">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Production Machine Assigned</Form.Label>
            <Form.Control
              type="text"
              placeholder="Machine Name (e.g. Konica C3070)"
              value={jobCardForm.machine}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, machine: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardMaterial">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Paper / Material stock details</Form.Label>
            <Form.Control
              type="text"
              placeholder="Material details (e.g. 300GSM Glossy)"
              value={jobCardForm.material}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, material: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardFinishing">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Finishing & Cutting Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Finishing details (e.g. Round cornering, crease line, fold)"
              value={jobCardForm.finishingInstructions}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, finishingInstructions: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardPriority">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Production Priority</Form.Label>
            <Form.Select
              value={jobCardForm.priority}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, priority: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent Priority</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formJobCardNotes">
            <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Special Production Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Any other special requests or instructions"
              value={jobCardForm.notes}
              onChange={(e) => setJobCardForm(prev => ({ ...prev, notes: e.target.value }))}
              style={{ borderRadius: '8px', fontSize: '13.5px' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0 px-4 pb-4">
          <Button variant="outline-secondary" onClick={onHide} style={{ borderRadius: '8px', fontSize: '13px' }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant={confirmSaveJobCardId === (isEditingJobCard ? selectedJobCard.id : 'new') ? 'danger' : 'primary'} 
            style={{ borderRadius: '8px', fontSize: '13px' }}
          >
            {confirmSaveJobCardId === (isEditingJobCard ? selectedJobCard.id : 'new')
              ? <><i className="fas fa-exclamation-triangle me-1" />Confirm Save</>
              : (isEditingJobCard ? 'Save Job Card' : 'Generate Job Card')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default JobCardModal;
